/**
 * Corner toast for "done, it's running" moments — appears, then fades out by
 * itself (no close button to hunt for). Fire from anywhere with `showToast()`;
 * `<Toaster />` is mounted once in App.
 */

import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const EVENT = "kra:toast";
const VISIBLE_MS = 3500;

interface ToastItem {
  id: number;
  message: string;
}

let nextId = 1;

/** Show a short message in the bottom-right corner for a few seconds. */
export function showToast(message: string): void {
  window.dispatchEvent(new CustomEvent<ToastItem>(EVENT, { detail: { id: nextId++, message } }));
}

export function Toaster() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  useEffect(() => {
    const onToast = (e: Event) => {
      const toast = (e as CustomEvent<ToastItem>).detail;
      setToasts((prev) => [...prev, toast]);
      window.setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== toast.id)), VISIBLE_MS);
    };
    window.addEventListener(EVENT, onToast);
    return () => window.removeEventListener(EVENT, onToast);
  }, []);

  return (
    <div className="pointer-events-none fixed bottom-6 right-4 z-[80] flex flex-col items-end gap-2 md:right-6">
      <AnimatePresence>
        {toasts.map((t) => (
          <motion.div
            key={t.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2 }}
            role="status"
            className="max-w-xs rounded-2xl bg-ink-0/90 px-4 py-2.5 text-xs font-medium text-surface-0 shadow-lg backdrop-blur-sm"
          >
            {t.message}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
